/**
 * Prices for the journey screens, read from `subscriptionCatalog` - what the
 * app actually charges - never from the design reference's fixtures.
 *
 * Display strings come from the catalog (`$14.99/mo`, `$119.99/year`); only the
 * trailing period suffix is removed, because the screens render the period as
 * separately translated text ("/ month", "billed yearly"). Currency is never
 * re-formatted by hand. Arabic strings isolate `{{price}}` with FSI/PDI.
 */
import type {
  BillingInterval,
  PaidPlanTier,
  PlanPrice,
  PlanSet,
  PlanTier,
  SubscriptionCatalog,
} from '@tabbio/types';

export type BillingCycle = BillingInterval;

export interface TierPrices {
  tier: PaidPlanTier;
  currency: string;
  /** e.g. "$14.99" */
  monthlyDisplay: string;
  monthlyAmount: number;
  /** Yearly plan expressed per month, e.g. "$9.99". */
  yearlyMonthlyEquivalentDisplay: string | null;
  /** Full yearly charge, e.g. "$119.99". */
  yearlyTotalDisplay: string | null;
  yearlyAmount: number | null;
  /** Whole percent saved by paying yearly; null when unknown or not positive. */
  savingsPercent: number | null;
}

/** "$14.99/mo" -> "$14.99", "$119.99/year" -> "$119.99", "AED 0/mo" -> "AED 0". */
export function stripPeriodSuffix(display: string) {
  return display.replace(/\s*\/.*$/, '').trim();
}

/**
 * round(100 x (12 x monthly - yearly) / (12 x monthly)). Same-currency only;
 * returns null for unknown, zero or negative savings so no "Save 0%" chip shows.
 */
export function computeSavingsPercent(
  monthlyAmount: number | null | undefined,
  yearlyAmount: number | null | undefined,
): number | null {
  if (
    typeof monthlyAmount !== 'number' ||
    typeof yearlyAmount !== 'number' ||
    !Number.isFinite(monthlyAmount) ||
    !Number.isFinite(yearlyAmount) ||
    monthlyAmount <= 0 ||
    yearlyAmount <= 0
  ) {
    return null;
  }
  const annualAtMonthly = 12 * monthlyAmount;
  const percent = Math.round(
    (100 * (annualAtMonthly - yearlyAmount)) / annualAtMonthly,
  );
  return percent > 0 ? percent : null;
}

function firstPrice(prices: PlanPrice[] | undefined) {
  return prices?.[0] ?? null;
}

export function getTierPrices(
  catalog: SubscriptionCatalog,
  planSet: PlanSet,
  tier: PlanTier,
): TierPrices | null {
  if (tier === 'free') return null;
  const definition = catalog.planSets[planSet]?.tiers[tier];
  const monthly = firstPrice(definition?.billing.monthly?.prices);
  if (!monthly) return null;
  const yearly = firstPrice(definition?.billing.yearly?.prices);
  const yearlyTotal =
    yearly && typeof yearly.billedAmount === 'number' && yearly.billedDisplay
      ? { amount: yearly.billedAmount, display: yearly.billedDisplay }
      : null;
  const sameCurrency = !yearly || yearly.currency === monthly.currency;

  return {
    tier,
    currency: monthly.currency,
    monthlyDisplay: stripPeriodSuffix(monthly.display),
    monthlyAmount: monthly.amount,
    yearlyMonthlyEquivalentDisplay:
      yearly && sameCurrency ? stripPeriodSuffix(yearly.display) : null,
    yearlyTotalDisplay:
      yearlyTotal && sameCurrency ? stripPeriodSuffix(yearlyTotal.display) : null,
    yearlyAmount: yearlyTotal && sameCurrency ? yearlyTotal.amount : null,
    savingsPercent: sameCurrency
      ? computeSavingsPercent(monthly.amount, yearlyTotal?.amount)
      : null,
  };
}

/**
 * What a cycle costs: the charge (checkout "Total today", disclosure line) and
 * the per-month figure (paywall compact price). Null when the catalog has no
 * product for that cycle - the UI must not offer it.
 */
export function getCyclePrice(
  prices: TierPrices | null,
  cycle: BillingCycle,
): { chargeDisplay: string; perMonthDisplay: string } | null {
  if (!prices) return null;
  if (cycle === 'monthly') {
    return {
      chargeDisplay: prices.monthlyDisplay,
      perMonthDisplay: prices.monthlyDisplay,
    };
  }
  if (!prices.yearlyTotalDisplay || !prices.yearlyMonthlyEquivalentDisplay) {
    return null;
  }
  return {
    chargeDisplay: prices.yearlyTotalDisplay,
    perMonthDisplay: prices.yearlyMonthlyEquivalentDisplay,
  };
}

/** i18n key for the "{{price}} billed monthly/yearly. Renews until canceled." line. */
export function disclosureKey(cycle: BillingCycle) {
  return cycle === 'yearly'
    ? 'subscription.paywall.disclosureYearly'
    : 'subscription.paywall.disclosureMonthly';
}
